import React, { memo } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { NO_ACCESS_LIMIT } from 'constants/feature_limits'
import { sendCtaClickEvent } from 'helpers/segment'
import { ImageStack } from 'ui_elements/ImageStack/ImageStack'
import { formatImageResolution } from 'helpers/images'
import { PRODUCTS_URL, PRODUCT_COSTS_AND_SETTINGS_URL } from 'constants/routes'
import { hasAccess } from 'helpers/permissions'
import { PERMISSION_KEYS } from 'constants/account/permissions'
import { Features } from 'constants/feature_names'
import { isFeatureEnabled } from 'helpers/features'

const ProductCellWrapper = styled.div`
  display: flex;

  [class*='ImageContainer'] {
    margin-right: 8px;
    width: 62px;
    height: 62px;
  }
`
const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 160px;
  line-height: 20px;
`
const Title = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-clamp: 2;
  -webkit-box-orient: vertical;
`
const ActionCopy = styled.span`
  color: ${props => props.theme.colors.grey600};
`
const LinkWrapper = styled(Link)`
  font-size: 11px;
  font-weight: 500;
  margin-top: 5px;
  color: ${props => props.theme.colors.primary};
`

/**
 * Returns Product data (image, title of the product and ability to adjust review request settings) for the given order.
 * This component is being used for the main row and sub-rows. In case there are multiple products in the order -
 * parent row will show an image stack (only collapsed view)
 * @param rowData
 * @param isSubRow
 * @param isExpanded - for parent row only
 * @returns {JSX.Element}
 */
function OrderProductCellComponent({ rowData, isSubRow, isExpanded }) {
  const inventoryLimit = useSelector(
    state => state.globalData.featureLimits.inventoryManager.featureLimit
  )
  const permissions = useSelector(state => state.globalData.user.permissions)
  const myProductsAccess = hasAccess(permissions, PERMISSION_KEYS.MY_PRODUCTS)
  const renderProductSettings =
    inventoryLimit !== NO_ACCESS_LIMIT && myProductsAccess

  const { t } = useTranslation('review_automation')
  const lineItems = rowData.line_items
  const lineItemsLength = lineItems?.length
  const product = isSubRow ? rowData : lineItems[0]

  const flagData = useSelector(state => state.globalData.flagData)
  const ProductCostsAndSettingsEnabled = isFeatureEnabled(
    Features.PRODUCT_SETTINGS_V2_TABLE,
    flagData
  )

  const SETTINGS_URL = ProductCostsAndSettingsEnabled
    ? PRODUCT_COSTS_AND_SETTINGS_URL
    : PRODUCTS_URL

  const destination = `${SETTINGS_URL}?asin=${product.asin}&prev=reviewAutomation`

  function renderActionCopy() {
    return product.review_request_enabled === false ? (
      <>
        <ActionCopy>
          {t(
            'review_automation:ReviewAutomationTable.product.dontSend',
            'Do not send request '
          )}
        </ActionCopy>
        &nbsp;
        <span>{t('generic:Edit', 'Edit')}...</span>
      </>
    ) : (
      t(
        'review_automation:ReviewAutomationTable.product.edit',
        'Edit Product Settings'
      )
    )
  }

  return (
    <ProductCellWrapper>
      <ImageStack
        src={formatImageResolution(product.amazon_image_url)}
        alt={product.title || 'n/a'}
        badgeNumber={isExpanded ? 0 : lineItemsLength} // Hide badge and image stack when parent row is expanded
        showStack={lineItemsLength > 1 && !isExpanded}
        isSingleStack
      />
      <TitleWrapper>
        <Title>{product.title}</Title>
        {renderProductSettings && (
          <LinkWrapper
            onClick={() => {
              sendCtaClickEvent({
                type: 'link',
                text: 'Product Column - Edit Product Settings',
                location: 'Review Automation',
                destination
              })
            }}
            to={destination}>
            {renderActionCopy()}
          </LinkWrapper>
        )}
      </TitleWrapper>
    </ProductCellWrapper>
  )
}

OrderProductCellComponent.defaultProps = {
  isSubRow: false,
  isExpanded: false
}
OrderProductCellComponent.propTypes = {
  rowData: PropTypes.objectOf(PropTypes.any).isRequired,
  isSubRow: PropTypes.bool,
  isExpanded: PropTypes.bool
}
OrderProductCellComponent.whyDidYouRender = true

const OrderProductCell = memo(OrderProductCellComponent)
export { OrderProductCell }
